import { z } from 'zod';
import { PdfStatusSchema } from './deposits.js';
import { ReceiptSchema } from './receipts.js';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// ───────────────────────────── Cash registers ──────────────────────────────
/** A configured physical cash drawer. One register ↔ one location at v1. */
export const CashRegisterSchema = z.object({
    id: z.number(),
    location_id: z.number(),
    project_id: z.number(),
    name: z.string(),
    /** Standing float = the opening balance; carries forward across closings. */
    float_amount: z.number(),
    is_active: z.boolean(),
});
export const CashRegisterUpsertSchema = z.object({
    location_id: z.number(),
    /** Ignored unless the box manages projects. */
    project_id: z.number().optional(),
    name: z.string().min(1).max(120),
    float_amount: z.number().min(0),
    is_active: z.boolean().optional(),
});
/** Live drawer state — recomputed from Arbox on every read (no stored balance).
 *  current_balance = opening_balance + cash_sales_total. */
export const RegisterCurrentSchema = z.object({
    cash_register_id: z.number(),
    name: z.string(),
    opening_balance: z.number(),
    cash_sales_total: z.number(),
    current_balance: z.number(),
    /** How many un-closed cash receipts make up cash_sales_total. */
    receipt_count: z.number(),
    currency: z.string(),
    /** The raw cash receipts currently accumulated in the drawer (un-closed). */
    receipts: z.array(ReceiptSchema),
});
// ─────────────────────────── Register closings ──────────────────────────────
/** 'closed' = a ₪0 closing (nothing to move to the safe): documented with a
 *  number + report, but never an envelope in the safe. */
export const ClosingStatusSchema = z.enum(['in_safe', 'closed', 'deposited', 'cancelled']);
export const CreateClosingRequestSchema = z.object({
    cash_register_id: z.number(),
    closing_date: z.string().regex(ISO_DATE),
    /** The cash receipts (external_receipt_id) being closed into this envelope.
     *  Lets the user close a subset and leave the rest in the register. The
     *  server re-fetches + validates them against the register's live cash.
     *  May be empty: a zero closing documents an end-of-shift drawer with no cash
     *  sales (the "Z" is still produced, with ₪0 to the safe). */
    external_receipt_ids: z.array(z.string()),
    /** What the user physically counted in the drawer. */
    counted_amount: z.number().min(0),
    /** Amount left in the drawer for next day; defaults to the register float. */
    float_left: z.number().min(0),
    notes: z.string().max(1000).nullable().optional(),
});
export const ClosingSummarySchema = z.object({
    id: z.number(),
    location_id: z.number(),
    project_id: z.number(),
    cash_register_id: z.number(),
    register_name: z.string().nullable(),
    closing_number: z.number(),
    closing_date: z.string(),
    opening_balance: z.number(),
    cash_sales_total: z.number(),
    expected_cash: z.number(),
    counted_amount: z.number(),
    /** counted_amount − expected_cash. Positive = over, negative = short. */
    variance: z.number(),
    float_left: z.number(),
    amount_to_safe: z.number(),
    currency: z.string(),
    status: ClosingStatusSchema,
    bank_deposit_id: z.number().nullable(),
    bank_deposit_number: z.number().nullable(),
    pdf_status: PdfStatusSchema,
    cancelled_at: z.string().nullable(),
    notes: z.string().nullable(),
    created_by_name: z.string().nullable(),
    created_at: z.string(),
});
export const ClosingReceiptSchema = z.object({
    id: z.number(),
    external_receipt_id: z.string(),
    receipt_number: z.string(),
    customer_name: z.string(),
    customer_external_id: z.string().nullable(),
    amount: z.number(),
    issue_date: z.string(),
});
export const ClosingDetailSchema = z.object({
    closing: ClosingSummarySchema,
    receipts: z.array(ClosingReceiptSchema),
});
export const ListClosingsQuerySchema = z.object({
    location_id: z.coerce.number(),
    project_id: z.coerce.number().optional(),
    from: z.string().regex(ISO_DATE),
    to: z.string().regex(ISO_DATE),
    status: z.enum(['all', 'in_safe', 'closed', 'deposited', 'cancelled']).default('all'),
});
export const CancelClosingRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
// ─────────────────────────── Bank deposits (safe) ───────────────────────────
/** The safe = the pool of in_safe envelopes for a location. */
export const SafeStateSchema = z.object({
    location_id: z.number(),
    project_id: z.number().nullable(),
    balance: z.number(),
    currency: z.string(),
    envelopes: z.array(ClosingSummarySchema),
});
export const CreateBankDepositRequestSchema = z.object({
    location_id: z.number(),
    project_id: z.number().optional(),
    deposit_date: z.string().regex(ISO_DATE),
    depositor_arbox_users_boxes_id: z.number().nullable().optional(),
    notes: z.string().max(1000).nullable().optional(),
    /** in_safe closing ids to deposit. */
    closing_ids: z.array(z.number()).min(1),
});
export const BankDepositSummarySchema = z.object({
    id: z.number(),
    location_id: z.number(),
    project_id: z.number(),
    deposit_number: z.number(),
    deposit_date: z.string(),
    total: z.number(),
    currency: z.string(),
    pdf_status: PdfStatusSchema,
    cancelled_at: z.string().nullable(),
    notes: z.string().nullable(),
    depositor_name: z.string().nullable(),
    created_by_name: z.string().nullable(),
    created_at: z.string(),
    closing_count: z.number(),
});
export const BankDepositDetailSchema = z.object({
    deposit: BankDepositSummarySchema,
    closings: z.array(ClosingSummarySchema),
});
export const ListBankDepositsQuerySchema = z.object({
    location_id: z.coerce.number(),
    project_id: z.coerce.number().optional(),
    from: z.string().regex(ISO_DATE),
    to: z.string().regex(ISO_DATE),
    status: z.enum(['all', 'active', 'cancelled']).default('all'),
});
export const CancelBankDepositRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=cash-registers.js.map